import { useState, useEffect, useCallback, useMemo } from "react";
import { ListTodo, Plus, Check, Trash2, Sparkles, Calendar, FileText, Info, PlusCircle } from "lucide-react";
import confetti from "canvas-confetti";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import TodoDetailsDialog from "@/components/TodoDetailsDialog";

const AVATAR_COLORS = [
  "bg-sky-200 text-sky-800",
  "bg-amber-200 text-amber-800",
  "bg-emerald-200 text-emerald-800",
  "bg-rose-200 text-rose-800",
  "bg-violet-200 text-violet-800",
  "bg-orange-200 text-orange-800",
];

function initialsOf(name) {
  if (!name) return "?";
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
}

function colorOf(id) {
  if (!id) return AVATAR_COLORS[0];
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function playApplause() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const duration = 0.4; // short applause
    const bufferSize = Math.floor(ctx.sampleRate * duration);
    const noise = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = noise.getChannelData(0);
    // Generate a quick burst of ~5-6 claps that decay fast
    for (let i = 0; i < bufferSize; i++) {
      const t = i / ctx.sampleRate;
      const burstFreq = 16; // claps per second
      const burst = Math.pow(Math.sin(Math.PI * t * burstFreq), 2);
      const decay = Math.exp(-t * 5); // fast decay so it ends crisp
      data[i] = (Math.random() * 2 - 1) * burst * decay;
    }
    const source = ctx.createBufferSource();
    source.buffer = noise;
    // Bandpass filter for clap timbre
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = 1600;
    filter.Q.value = 1.1;
    // Master gain
    const gain = ctx.createGain();
    gain.gain.value = 0.28;
    source.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    source.start();
    source.stop(ctx.currentTime + duration);
    setTimeout(() => ctx.close(), (duration + 0.15) * 1000);
  } catch { /* fail silently in browsers without Web Audio */ }
}

function celebrate() {
  playApplause();
  try {
    const end = Date.now() + 600;
    const colors = ["#f59e0b", "#10b981", "#3b82f6", "#ec4899", "#ef4444"];
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors, scalar: 0.9 });
      confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors, scalar: 0.9 });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  } catch { /* ignore */ }
}

/** TodoList — for Home Edition only. Supports assignees, applause on completion. */
export default function TodoList() {
  const { user, activeBoat } = useAuth();
  const [todos, setTodos] = useState([]);
  const [newText, setNewText] = useState("");
  const [newAssignee, setNewAssignee] = useState("");
  const [filter, setFilter] = useState("all"); // all | mine
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [detailedAddOpen, setDetailedAddOpen] = useState(false);
  const isHousehold = (activeBoat?.kind || "boat") === "household";

  const members = useMemo(() => {
    const mem = activeBoat?.members || [];
    return mem
      .filter((m) => !m.is_global_admin)
      .map((m) => ({ id: m.user_id, name: m.name || m.email?.split("@")[0] || "?" }));
  }, [activeBoat]);

  const load = useCallback(async () => {
    try {
      const res = await api.getTodos();
      setTodos(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke laste gjøremål");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addTodo = async () => {
    const text = newText.trim();
    if (!text) return;
    setAdding(true);
    try {
      const payload = { text };
      if (newAssignee) {
        const m = members.find((x) => x.id === newAssignee);
        payload.assignee_user_id = newAssignee;
        payload.assignee_name = m?.name || "";
      }
      const res = await api.createTodo(payload.text);
      // Patch assignee immediately after create (separate call so /todos signature stays simple)
      if (newAssignee) {
        const upd = await api.updateTodo(res.data.id, { assignee_user_id: newAssignee, assignee_name: payload.assignee_name });
        setTodos((prev) => [upd.data, ...prev]);
      } else {
        setTodos((prev) => [res.data, ...prev]);
      }
      setNewText("");
      setNewAssignee("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke legge til");
    } finally { setAdding(false); }
  };

  const toggle = async (t) => {
    const willBeDone = !t.done;
    try {
      const res = await api.updateTodo(t.id, { done: willBeDone });
      setTodos((prev) => prev.map((x) => (x.id === t.id ? res.data : x)));
      if (willBeDone) {
        // Applaus + konfetti KUN for Home Edition — ikke båt.
        if (isHousehold) celebrate();
        toast.success(`${isHousehold ? "🎉 " : ""}Bra jobba! «${t.text.slice(0, 40)}»`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke oppdatere");
    }
  };

  const changeAssignee = async (t, assigneeId) => {
    const m = members.find((x) => x.id === assigneeId);
    try {
      const res = await api.updateTodo(t.id, {
        assignee_user_id: assigneeId || null,
        assignee_name: m?.name || "",
      });
      setTodos((prev) => prev.map((x) => (x.id === t.id ? res.data : x)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil");
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteTodo(id);
      setTodos((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke slette");
    }
  };

  const clearDone = async () => {
    try {
      const res = await api.clearDoneTodos();
      setTodos((prev) => prev.filter((t) => !t.done));
      toast.success(`${res.data.deleted} ferdige fjernet`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil");
    }
  };

  const visible = filter === "mine"
    ? todos.filter((t) => t.assignee_user_id === user?.user_id)
    : todos;
  const active = visible.filter((t) => !t.done);
  const done = visible.filter((t) => t.done);

  return (
    <div className="bg-white border border-stone-100 rounded-xl p-4" data-testid="todo-list">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-sky-50 flex items-center justify-center">
            <ListTodo className="h-5 w-5 text-sky-600" />
          </div>
          <div>
            <div className="text-base font-semibold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Hjemme-gjøremål</div>
            <div className="text-[11px] text-stone-400">{active.length} aktive · {done.length} ferdig</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-stone-100 rounded-full p-0.5" data-testid="todo-filter">
            <button
              onClick={() => setFilter("all")}
              className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2.5 py-1 transition-colors ${filter === "all" ? "bg-white text-stone-800 shadow-sm" : "text-stone-400"}`}
              data-testid="filter-all"
            >Alle</button>
            <button
              onClick={() => setFilter("mine")}
              className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2.5 py-1 transition-colors ${filter === "mine" ? "bg-white text-stone-800 shadow-sm" : "text-stone-400"}`}
              data-testid="filter-mine"
            >Mine</button>
          </div>
          {done.length > 0 && (
            <button
              onClick={clearDone}
              className="text-[11px] text-stone-400 hover:text-red-500 font-semibold"
              data-testid="clear-done-todos-btn"
            >Tøm ferdige</button>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-3">
        <input
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addTodo()}
          placeholder="Legg til gjøremål…"
          className="flex-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500"
          data-testid="todo-input"
        />
        {members.length > 0 && (
          <select
            value={newAssignee}
            onChange={(e) => setNewAssignee(e.target.value)}
            className="rounded-md border border-stone-200 bg-white px-2 py-2 text-xs outline-none focus:border-sky-500 max-w-[140px]"
            data-testid="todo-assignee-select"
          >
            <option value="">— Tildel til —</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        )}
        <button
          onClick={addTodo}
          disabled={adding || !newText.trim()}
          className="rounded-md bg-sky-600 text-white px-3 py-2 text-sm font-semibold hover:bg-sky-700 disabled:opacity-50 inline-flex items-center gap-1"
          data-testid="todo-add-btn"
          title="Legg til enkelt gjøremål"
        >
          <Plus className="h-4 w-4" />
        </button>
        <button
          onClick={() => setDetailedAddOpen(true)}
          className="rounded-md border border-sky-200 bg-sky-50 text-sky-700 px-3 py-2 text-xs font-semibold hover:bg-sky-100 inline-flex items-center gap-1"
          data-testid="todo-add-detailed-btn"
          title="Ny oppgave med beskrivelse og frist"
        >
          <PlusCircle className="h-4 w-4" /> Med detaljer
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-stone-400 text-center py-4">Laster...</div>
      ) : visible.length === 0 ? (
        <div className="text-xs text-stone-400 text-center py-4 italic flex items-center justify-center gap-1">
          <Sparkles className="h-3 w-3" /> {filter === "mine" ? "Ingen tildelt til deg" : "Ingen gjøremål ennå"}
        </div>
      ) : (
        <ul className="space-y-1" data-testid="todo-items">
          {[...active, ...done].map((t) => {
            const assignee = members.find((m) => m.id === t.assignee_user_id);
            const name = assignee?.name || t.assignee_name;
            return (
              <li key={t.id} className="flex items-center gap-2 rounded-md hover:bg-stone-50 px-2 py-1.5 group" data-testid={`todo-${t.id}`}>
                <button
                  onClick={() => toggle(t)}
                  className={`h-5 w-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${t.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-stone-300 hover:border-sky-500"}`}
                  data-testid={`todo-toggle-${t.id}`}
                  aria-label={t.done ? "Marker som ikke gjort" : "Marker som ferdig"}
                >
                  {t.done && <Check className="h-3 w-3" />}
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedTodo(t)}
                  className={`flex-1 min-w-0 text-left text-sm inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 ${t.done ? "line-through text-stone-400" : "text-stone-700"} hover:bg-sky-50 hover:text-sky-700 transition-colors group/row`}
                  data-testid={`todo-open-${t.id}`}
                  title="Klikk for detaljer og beskrivelse"
                >
                  <span className="truncate">{t.text}</span>
                  {t.description && (
                    <FileText className="h-3 w-3 text-amber-500 flex-shrink-0" title="Har beskrivelse" />
                  )}
                  {t.due_date && (() => {
                    const due = new Date(t.due_date);
                    const overdue = !t.done && due < new Date(new Date().setHours(0,0,0,0));
                    return (
                      <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold flex-shrink-0 ${overdue ? "text-red-600" : "text-stone-400"}`}>
                        <Calendar className="h-3 w-3" />
                        {due.toLocaleDateString("nb-NO", { day: "2-digit", month: "short" })}
                      </span>
                    );
                  })()}
                  <Info className="h-3 w-3 text-stone-300 flex-shrink-0 opacity-0 group-hover:opacity-100 group-hover/row:opacity-100 transition-opacity ml-auto" aria-label="Klikk for detaljer" />
                </button>
                {/* Assignee avatar / picker */}
                {members.length > 0 ? (
                  <div className="relative flex-shrink-0">
                    <select
                      value={t.assignee_user_id || ""}
                      onChange={(e) => changeAssignee(t, e.target.value)}
                      className="appearance-none bg-transparent text-[11px] font-semibold text-stone-400 rounded-md pl-1 pr-1 py-0.5 border border-transparent hover:border-stone-200 cursor-pointer"
                      data-testid={`assignee-${t.id}`}
                      aria-label="Tildel til"
                      title={name ? `Tildelt: ${name}` : "Ingen tildelt"}
                    >
                      <option value="">Ingen</option>
                      {members.map((m) => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                ) : null}
                {name && (
                  <span
                    className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-[10px] font-bold flex-shrink-0 ${colorOf(t.assignee_user_id || t.assignee_name)}`}
                    title={`Tildelt: ${name}${t.done && t.completed_by_name ? ` · Utført av ${t.completed_by_name}` : ""}`}
                    data-testid={`avatar-${t.id}`}
                  >
                    {initialsOf(name)}
                  </span>
                )}
                <button
                  onClick={() => remove(t.id)}
                  className="text-stone-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`todo-delete-${t.id}`}
                  aria-label="Slett"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <TodoDetailsDialog
        open={!!selectedTodo}
        onOpenChange={(o) => { if (!o) setSelectedTodo(null); }}
        todo={selectedTodo}
        members={members}
        onChanged={(updated) => {
          if (updated === null) {
            setTodos((p) => p.filter((t) => t.id !== selectedTodo?.id));
            setSelectedTodo(null);
          } else {
            setTodos((p) => p.map((t) => (t.id === updated.id ? updated : t)));
            setSelectedTodo(updated);
          }
        }}
        onToggleDone={(t) => {
          toggle(t);
          if (!t.done) setSelectedTodo(null); // lukke etter completion så brukeren ser applausen
        }}
      />
      <DetailedTodoCreateDialog
        open={detailedAddOpen}
        onOpenChange={setDetailedAddOpen}
        members={members}
        onCreated={(newTodo) => {
          setTodos((p) => [newTodo, ...p]);
        }}
      />
    </div>
  );
}

// Separate create-with-details dialog (lightweight wrapper around /todos POST)
function DetailedTodoCreateDialog({ open, onOpenChange, members, onCreated }) {
  const [form, setForm] = useState({ text: "", description: "", due_date: "", assignee_user_id: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) setForm({ text: "", description: "", due_date: "", assignee_user_id: "" });
  }, [open]);

  const save = async () => {
    if (!form.text.trim()) { toast.error("Skriv en tittel"); return; }
    setSaving(true);
    try {
      const m = members.find((x) => x.id === form.assignee_user_id);
      const res = await api.createTodo({
        text: form.text.trim(),
        description: form.description.trim() || null,
        due_date: form.due_date || null,
        assignee_user_id: form.assignee_user_id || null,
        assignee_name: m?.name || null,
      });
      toast.success("Oppgave opprettet");
      onCreated?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke opprette");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="todo-detailed-create-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ny oppgave med detaljer</DialogTitle>
          <DialogDescription>Skriv det du vil huske — beskrivelse, frist og tildeling er valgfritt.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-stone-500 uppercase">Tittel *</label>
            <input
              autoFocus
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              placeholder="F.eks. Klippe gress, Bestille tannlegetime"
              className="w-full mt-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none"
              data-testid="detailed-todo-title"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-stone-500 uppercase">Beskrivelse</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Hva skal gjøres? Framgangsmåte, notater, hvem ringe osv…"
              className="w-full mt-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none resize-none min-h-[100px]"
              data-testid="detailed-todo-description"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-stone-500 uppercase flex items-center gap-1"><Calendar className="h-3 w-3" /> Frist</label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="w-full mt-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none"
                data-testid="detailed-todo-due"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-stone-500 uppercase">Tildel til</label>
              <select
                value={form.assignee_user_id}
                onChange={(e) => setForm({ ...form, assignee_user_id: e.target.value })}
                className="w-full mt-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none"
                data-testid="detailed-todo-assignee"
              >
                <option value="">Ingen</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button
              onClick={() => onOpenChange(false)}
              className="flex-1 rounded-md border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors"
              data-testid="detailed-todo-cancel"
            >Avbryt</button>
            <button
              onClick={save}
              disabled={saving || !form.text.trim()}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 transition-colors disabled:opacity-50"
              data-testid="detailed-todo-save"
            >
              <PlusCircle className="h-4 w-4" /> {saving ? "Lagrer…" : "Opprett"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

